import { memo, useEffect, useRef, useState } from 'react';
import type { DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BoardButton, BoardFrame, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Era } from '../../art.js';
import { GROUND, LEAF_D, SPECKS, clamp, demoRound, feedbackTo, lerp, playDemo, wave } from './garden.js';
import type { Feedback } from './garden.js';

/**
 * BIOTECHNICIAN · GROW THE VINE — steer a growing vine onto three seeds.
 *
 * The classic Vine, grown up: the vine creeps a cell at a time across a bed ten cells by seven,
 * and leaves unfurl behind its tip. A swipe anywhere on the board turns it, and so do the four
 * arrows under the bed (and a laptop's arrow keys). The bed's edge only stops it (turn and it goes
 * on), so nothing here ends a run. Three seeds a round, one at a time: round 1 creeps slowly, each
 * round after a little quicker. (Rocks in the bed, the old round 3, are still in the rules —
 * growing into one is the mistake, and the vine stops short for a moment before it can be turned
 * away; no round lays them since 6 Oct 2026.) Underground (the dust era) the vine is a root.
 */

export const COLS = 10;
export const ROWS = 7;

export type Dir = 'up' | 'down' | 'left' | 'right';
export interface Cell {
  x: number;
  y: number;
}
/** A cell of the vine, with the step it grew on (so its leaf stays put as the vine creeps). */
export interface Part extends Cell {
  n: number;
}

export interface VineLevel {
  /** One cell every so many milliseconds. */
  stepMs: number;
  seeds: number;
  rocks: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const VINE_LEVELS: readonly VineLevel[] = easier(
  [
    { stepMs: 420, seeds: 3, rocks: 0 },
    { stepMs: 340, seeds: 3, rocks: 0 },
  ],
);

/** The vine stands still this long at first, so the bed can be read. */
const START_MS = 1_000;
/** A bump into a rock holds the vine up this long. */
const BUMP_MS = 800;
/** The bumped rock shakes this long. */
const SHAKE_MS = 420;
/** A pressed arrow shows pressed this long. */
const PRESS_MS = 160;
/** A finger that moves this far (board units) is a swipe. */
const SWIPE = 18;

/** Where the vine sets out from, heading right. */
export const START: Cell = { x: 1, y: 3 };

/** How long the vine is once it has eaten `got` seeds. */
export const lengthFor = (got: number) => 3 + got * 2;

const STEP: Record<Dir, Cell> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};
export const OPPOSITE: Record<Dir, Dir> = { up: 'down', down: 'up', left: 'right', right: 'left' };

export const nextCell = (c: Cell, d: Dir): Cell => ({ x: c.x + STEP[d].x, y: c.y + STEP[d].y });
export const same = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;

/** What stops the vine going into `c`: the bed's edge, a rock, or nothing. */
export function blocked(c: Cell, rocks: readonly Cell[], cols = COLS, rows = ROWS): 'edge' | 'rock' | null {
  if (c.x < 0 || c.y < 0 || c.x >= cols || c.y >= rows) return 'edge';
  return rocks.some((r) => same(r, c)) ? 'rock' : null;
}

/** The one turn a vine can't make: back into its own neck. */
export function intoNeck(body: readonly Cell[], d: Dir): boolean {
  const head = body[0];
  const neck = body[1];
  return !!head && !!neck && same(nextCell(head, d), neck);
}

/**
 * Rocks for a bed: inside it (never on the edge), never touching one another, not even at a
 * corner, and never in the row the vine sets out along. Rocks that never touch can't wall any
 * cell in, so every seed stays in reach.
 */
export function placeRocks(rand: () => number, count: number, cols = COLS, rows = ROWS): Cell[] {
  const out: Cell[] = [];
  let guard = 0;
  while (out.length < count && guard < 500) {
    guard += 1;
    const c = { x: 2 + Math.floor(rand() * (cols - 3)), y: 1 + Math.floor(rand() * (rows - 2)) };
    if (c.y === START.y) continue;
    if (out.some((r) => Math.abs(r.x - c.x) <= 1 && Math.abs(r.y - c.y) <= 1)) continue;
    out.push(c);
  }
  return out;
}

/** The next seed: not under the vine or a rock, a fair few cells from the tip but not across the bed. */
export function placeSeed(rand: () => number, body: readonly Cell[], rocks: readonly Cell[], cols = COLS, rows = ROWS): Cell {
  const head = body[0] ?? START;
  const free: Cell[] = [];
  for (let y = 0; y < rows; y += 1)
    for (let x = 0; x < cols; x += 1) {
      const c = { x, y };
      if (body.some((b) => same(b, c)) || rocks.some((r) => same(r, c))) continue;
      free.push(c);
    }
  const dist = (c: Cell) => Math.abs(c.x - head.x) + Math.abs(c.y - head.y);
  const near = free.filter((c) => dist(c) >= 3 && dist(c) <= 8);
  const pool = near.length > 0 ? near : free.filter((c) => dist(c) >= 2);
  const from = pool.length > 0 ? pool : free;
  return from[Math.floor(rand() * from.length)] ?? { x: cols - 2, y: head.y };
}

/** The steps a vine needs to reach `to` round the rocks (edges and rocks block), or -1. */
export function stepsTo(from: Cell, to: Cell, rocks: readonly Cell[], cols = COLS, rows = ROWS): number {
  const seen = new Set<string>([`${from.x},${from.y}`]);
  let ring: Cell[] = [from];
  for (let d = 0; ring.length > 0; d += 1) {
    if (ring.some((c) => same(c, to))) return d;
    const next: Cell[] = [];
    for (const c of ring)
      for (const dir of ['up', 'down', 'left', 'right'] as const) {
        const n = nextCell(c, dir);
        const k = `${n.x},${n.y}`;
        if (seen.has(k) || blocked(n, rocks, cols, rows)) continue;
        seen.add(k);
        next.push(n);
      }
    ring = next;
  }
  return -1;
}

/** The vine as it grows. */
export interface Vine {
  /** Its cells, tip first; each step it grows gets the next number. */
  body: Part[];
  dir: Dir;
  /** Turns asked for and not yet made, at most two (a quick U-turn round a rock). */
  queue: Dir[];
  got: number;
  seed: Cell | null;
  serial: number;
  /** When it last grew, and until when it stands still (the start, a bump). */
  lastStep: number;
  holdUntil: number;
  stuck: 'edge' | 'rock' | null;
  bumpAt: number;
  bumpRock: number;
}

/** A new vine: two cells long at the start, heading right, standing still until the round begins. */
export function sprout(seed: Cell | null): Vine {
  return {
    body: [
      { ...START, n: 2 },
      { x: START.x - 1, y: START.y, n: 1 },
    ],
    dir: 'right',
    queue: [],
    got: 0,
    seed,
    serial: 2,
    lastStep: 0,
    holdUntil: Number.POSITIVE_INFINITY,
    stuck: null,
    bumpAt: -1e9,
    bumpRock: -1,
  };
}

/** A turn asked for: queued, unless it is no turn at all, back into the neck, or a third. Was it? */
export function steer(v: Vine, d: Dir): boolean {
  const last = v.queue[v.queue.length - 1];
  if (last === undefined) {
    if (d === v.dir && v.stuck === null) return false;
    if (intoNeck(v.body, d)) return false;
  } else if (d === last || d === OPPOSITE[last] || v.queue.length >= 2) return false;
  v.queue.push(d);
  return true;
}

/** What a beat of growth did: grew a cell, ate the seed, stopped (at the edge, or still at a rock), or bumped a rock. */
export type Beat = 'grew' | 'seed' | 'stuck' | 'bump';

/** One beat: the vine takes the first turn it can, then grows a cell, or stops at the edge or a rock. */
export function grow(v: Vine, rocks: readonly Cell[], now: number): Beat {
  let d = v.dir;
  while (v.queue.length > 0) {
    const t = v.queue.shift()!;
    if (!intoNeck(v.body, t)) {
      d = t;
      break;
    }
  }
  v.dir = d;
  const next = nextCell(v.body[0]!, d);
  const b = blocked(next, rocks);
  if (b === 'edge') {
    v.stuck = 'edge';
    return 'stuck';
  }
  if (b === 'rock') {
    const fresh = v.stuck !== 'rock';
    if (fresh) {
      v.bumpAt = now;
      v.bumpRock = rocks.findIndex((r) => same(r, next));
      v.holdUntil = now + BUMP_MS;
    }
    v.stuck = 'rock';
    return fresh ? 'bump' : 'stuck';
  }
  v.stuck = null;
  v.serial += 1;
  v.body = [{ ...next, n: v.serial }, ...v.body].slice(0, lengthFor(v.got));
  if (v.seed && same(next, v.seed)) {
    v.got += 1;
    return 'seed';
  }
  return 'grew';
}

/** Everything the bed shows. */
export interface VineView {
  /** The game clock, ms. */
  t: number;
  /** The vine's cells, tip first (0–9 across, 0–6 down), and the tip's growth count (leaves alternate by it). */
  b: readonly (readonly [x: number, y: number])[];
  n: number;
  /** How far the tip has crept into the next cell (0–1), and the way it is heading. */
  p: number;
  d: Dir;
  /** Seeds eaten, and how many in all. */
  g: number;
  of: number;
  /** The seed waiting, or null. */
  s: readonly [x: number, y: number] | null;
  /** Rocks in the bed (no round lays them since 6 Oct 2026). */
  r: readonly (readonly [x: number, y: number])[];
  /** 0 growing, 1 against the edge, 2 just bumped a rock, 3 every seed eaten. */
  st: 0 | 1 | 2 | 3;
  /** The last bump, while the rock shakes: which rock, and ms ago. */
  bump: readonly [rock: number, age: number] | null;
  /** The arrow just pressed, shown pressed. */
  k: Dir | null;
  fx?: Fx | undefined;
}

/** The bed on the board: a cell is 10 bed units, the bed drawn 2.9 times that, the arrows under it. */
const C = 10;
const BED = { x: 15, y: 4, scale: 2.9 } as const;
const PAD_W = 88;
const PAD_H = 46;
/** The arrows under the bed, in board units: up above, then left, down and right. */
export const PAD: readonly { d: Dir; x: number; y: number }[] = [
  { d: 'up', x: 116, y: 214 },
  { d: 'left', x: 26, y: 265 },
  { d: 'down', x: 116, y: 265 },
  { d: 'right', x: 206, y: 265 },
];

/** The arrow under a point on the board, or null. */
export function padAt(p: Pt): Dir | null {
  return PAD.find((b) => p.x >= b.x && p.x <= b.x + PAD_W && p.y >= b.y && p.y <= b.y + PAD_H)?.d ?? null;
}

/**
 * A round of the vine, driven by a finger (a guest's or the how-to's) and the game clock. `seeds`
 * lays the seeds by hand (the how-to); otherwise each is dealt where the vine can reach it.
 */
export function vineRound(level: Level, rand: () => number, fb: Feedback, hand?: { seeds: readonly Cell[]; startMs: number }) {
  const L = VINE_LEVELS[level]!;
  const rocks = placeRocks(rand, L.rocks);
  const v = sprout(null);
  const nextSeed = () => (hand ? (hand.seeds[v.got] ?? null) : placeSeed(rand, v.body, rocks));
  v.seed = nextSeed();
  let started = false;
  let won = false;
  let pressed: { d: Dir; at: number } | null = null;
  let swipe: Pt | null = null;

  const turn = (d: Dir, now: number) => {
    if (won || !steer(v, d)) return;
    // Waiting at an edge or a rock: turn at once rather than on the next beat.
    if (v.stuck !== null && now >= v.holdUntil) v.lastStep = now - L.stepMs;
  };

  return {
    /** An arrow, on the board or the keyboard. */
    press(d: Dir, now: number) {
      pressed = { d, at: now };
      turn(d, now);
    },
    input: {
      down(p: Pt) {
        swipe = p;
      },
      move(p: Pt, now: number) {
        if (!swipe) return;
        const dx = p.x - swipe.x;
        const dy = p.y - swipe.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE) return;
        turn(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up', now);
        swipe = p;
      },
      up() {
        swipe = null;
      },
    },
    step(now: number) {
      if (!started) {
        started = true;
        v.lastStep = now;
        v.holdUntil = now + (hand?.startMs ?? START_MS);
      }
      if (won) return;
      if (now < v.holdUntil) {
        v.lastStep = now;
        return;
      }
      if (now - v.lastStep < L.stepMs) return;
      v.lastStep = now;
      const beat = grow(v, rocks, now);
      if (beat === 'bump') fb.miss();
      if (beat !== 'seed') return;
      fb.hit();
      fb.progress(v.got / L.seeds);
      if (v.got >= L.seeds) {
        v.seed = null;
        won = true;
        fb.win();
      } else v.seed = nextSeed();
    },
    view(now: number, fx: Fx | undefined): VineView {
      const head = v.body[0]!;
      const turnTo = v.queue.find((d) => !intoNeck(v.body, d)) ?? v.dir;
      const free = blocked(nextCell(head, turnTo), rocks) === null;
      const moving = started && !won && now >= v.holdUntil && free;
      const bumped = now - v.bumpAt;
      return {
        t: q(now),
        b: v.body.map((c) => [c.x, c.y] as const),
        n: head.n,
        p: moving ? q(clamp((now - v.lastStep) / L.stepMs), 2) : 0,
        d: free ? turnTo : v.dir,
        g: v.got,
        of: L.seeds,
        s: v.seed ? [v.seed.x, v.seed.y] : null,
        r: rocks.map((r) => [r.x, r.y] as const),
        st: won ? 3 : bumped < BUMP_MS ? 2 : v.stuck === 'edge' ? 1 : 0,
        bump: bumped < SHAKE_MS ? [v.bumpRock, q(bumped)] : null,
        k: pressed && now - pressed.at < PRESS_MS ? pressed.d : null,
        fx,
      };
    },
  };
}

/**
 * A stem through `pts` (tip first), smoothed round its corners and tapering from `wTip` to
 * `wTail`: its outline as one closed path, and its spine for the lit edge.
 */
export function ribbon(pts: readonly Pt[], wTip: number, wTail: number): { outline: string; spine: string } {
  if (pts.length === 0) return { outline: '', spine: '' };
  const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const line: Pt[] = [];
  const k = pts.length - 1;
  for (let i = 0; i <= k; i += 1) {
    const p = pts[i]!;
    const a = i === 0 ? p : mid(pts[i - 1]!, p);
    const b = i === k ? p : mid(p, pts[i + 1]!);
    for (let s = i === 0 ? 0 : 1; s <= 4; s += 1) {
      const u = s / 4;
      line.push({ x: (1 - u) * (1 - u) * a.x + 2 * (1 - u) * u * p.x + u * u * b.x, y: (1 - u) * (1 - u) * a.y + 2 * (1 - u) * u * p.y + u * u * b.y });
    }
  }
  const pts2 = line.filter((p, i) => i === 0 || Math.hypot(p.x - line[i - 1]!.x, p.y - line[i - 1]!.y) > 0.05);
  const n = pts2.length;
  if (n < 2) {
    const p = pts2[0]!;
    const r = wTip / 2;
    return { outline: `M${p.x - r} ${p.y} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0`, spine: '' };
  }
  const left: Pt[] = [];
  const right: Pt[] = [];
  for (let i = 0; i < n; i += 1) {
    const a = pts2[Math.max(0, i - 1)]!;
    const b = pts2[Math.min(n - 1, i + 1)]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const nx = -(b.y - a.y) / len;
    const ny = (b.x - a.x) / len;
    const w = (wTip + (wTail - wTip) * (i / (n - 1))) / 2;
    const p = pts2[i]!;
    left.push({ x: p.x + nx * w, y: p.y + ny * w });
    right.push({ x: p.x - nx * w, y: p.y - ny * w });
  }
  const f = (p: Pt) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  const rTip = wTip / 2;
  const rTail = Math.max(0.2, wTail / 2);
  const outline =
    `M${f(left[0]!)} ` +
    left.slice(1).map((p) => `L${f(p)}`).join(' ') +
    ` A${rTail} ${rTail} 0 0 0 ${f(right[n - 1]!)} ` +
    right
      .slice(0, n - 1)
      .reverse()
      .map((p) => `L${f(p)}`)
      .join(' ') +
    ` A${rTip} ${rTip} 0 0 0 ${f(left[0]!)} Z`;
  const spine = `M${pts2.map(f).join(' L')}`;
  return { outline, spine };
}

const centre = (x: number, y: number) => ({ x: x * C + C / 2, y: y * C + C / 2 });
const ANGLE: Record<Dir, number> = { right: 0, down: 90, left: 180, up: 270 };
const ARROW: Record<Dir, string> = { up: 'M0 -7 L7 3 L-7 3 Z', down: 'M0 7 L7 -3 L-7 -3 Z', left: 'M-7 0 L3 -7 L3 7 Z', right: 'M7 0 L-3 -7 L-3 7 Z' };
const ARROW_WORDS: Record<Dir, readonly [string, string]> = { up: ['Up', 'Opp'], left: ['Left', 'Venstre'], down: ['Down', 'Ned'], right: ['Right', 'Høyre'] };

/** The bed and the vine, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<VineView>) {
  const id = useSvgId();
  const g = GROUND[era];
  const root = era === 'dust';
  // A demo glides every number between its keys: counts and states are read rounded.
  const st = Math.round(view.st);
  const got = Math.round(view.g);
  const n = Math.round(view.n);
  const body = view.b;
  const p = clamp(view.p);
  const full = body.length >= lengthFor(got);

  // The stem's points, tip first: the tip creeps into the next cell, the tail draws in behind.
  const [hx, hy] = body[0]!;
  const hc = centre(hx, hy);
  const dv = STEP[view.d];
  const pts: Pt[] = [{ x: hc.x + dv.x * C * p, y: hc.y + dv.y * C * p }];
  for (let i = 0; i < body.length; i += 1) {
    const c = centre(body[i]![0], body[i]![1]);
    if (i === body.length - 1 && full && p > 0 && body.length > 1) {
      const prev = centre(body[i - 1]![0], body[i - 1]![1]);
      pts.push({ x: lerp(c.x, prev.x, p), y: lerp(c.y, prev.y, p) });
    } else if (i > 0 || p > 0) pts.push(c);
  }
  const stem = ribbon(pts, 3.1, 1.1);

  // A leaf at every cell, alternating sides, unfurling over the two steps after it grew.
  const leaves = body.slice(1).flatMap(([x, y], j) => {
    const i = j + 1;
    const grewOn = n - i;
    const [tx, ty] = body[i - 1]!;
    const ang = (Math.atan2(ty - y, tx - x) * 180) / Math.PI;
    const side = grewOn % 2 === 0 ? 1 : -1;
    let s = clamp((i + p - 0.4) / 2.2) * (grewOn % 4 < 2 ? 0.9 : 0.7);
    if (i === body.length - 1 && full) s *= 1 - p;
    if (s <= 0.02) return [];
    const c = centre(x, y);
    return [{ key: grewOn, x: c.x, y: c.y, rot: ang + side * 58, s }];
  });

  const status =
    st === 3
      ? { text: say(lang, '✓ ALL SEEDS', '✓ ALLE FRØ'), tone: 'ok' as const }
      : st === 2
        ? { text: say(lang, 'A ROCK — TURN AWAY', 'EN STEIN – SNU'), tone: 'bad' as const }
        : st === 1
          ? { text: say(lang, 'THE EDGE — TURN', 'KANTEN – SNU'), tone: 'warn' as const }
          : { text: say(lang, 'SEEDS', 'FRØ'), count: [got, view.of] as const, hint: say(lang, 'SWIPE OR TAP AN ARROW', 'SVEIP ELLER TRYKK PÅ PIL') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}soil`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={g.soilTop} />
          <stop offset="1" stopColor={g.soilBottom} />
        </linearGradient>
        <radialGradient id={`${id}seed`} cx=".4" cy=".35" r=".7">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset=".35" stopColor={g.glow} />
          <stop offset="1" stopColor={g.stemDark} />
        </radialGradient>
        <radialGradient id={`${id}halo`}>
          <stop offset="0" stopColor={g.glow} stopOpacity=".55" />
          <stop offset="1" stopColor={g.glow} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}leaf`} x1="0" y1="-1" x2="0" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor={g.leafHi} />
          <stop offset=".55" stopColor={g.leaf} />
          <stop offset="1" stopColor={g.stemDark} />
        </linearGradient>
        <linearGradient id={`${id}rock`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={g.rockHi} />
          <stop offset=".6" stopColor={g.rock} />
          <stop offset="1" stopColor="#1a1512" />
        </linearGradient>
      </defs>

      <g transform={`translate(${BED.x} ${BED.y}) scale(${BED.scale})`}>
        <BedArt era={era} id={id} />

        {/* rocks (no round lays them since 6 Oct 2026) */}
        {view.r.map(([rx, ry], i) => {
          const c = centre(rx, ry);
          const age = view.bump && Math.round(view.bump[0]) === i ? view.bump[1] : null;
          const shake = age !== null && age < SHAKE_MS ? Math.sin(age / 32) * 0.9 * (1 - age / SHAKE_MS) : 0;
          return (
            <g key={`${rx},${ry}`} transform={`translate(${(c.x + shake).toFixed(2)} ${c.y})`}>
              <ellipse cx="0.6" cy="3.6" rx="4.8" ry="1.5" fill="#000" opacity=".45" />
              <path d="M-4.4 1.9 L-3.6 -2.4 L-0.8 -4.2 L3 -3.4 L4.6 0.3 L3.4 3.4 L-1.6 3.9 Z" fill={`url(#${id}rock)`} stroke="#120e0b" strokeWidth=".4" />
              <path d="M-2.8 -2 L-0.7 -3.3 L2 -2.8" fill="none" stroke="#ffffff" strokeOpacity=".35" strokeWidth=".5" strokeLinecap="round" />
            </g>
          );
        })}

        {/* the seed, glowing softly and swaying */}
        {view.s && (
          <g transform={`translate(${centre(view.s[0], view.s[1]).x} ${centre(view.s[0], view.s[1]).y})`}>
            <circle r="7" fill={`url(#${id}halo)`} opacity={(0.35 + 0.65 * wave(view.t, 2_800)).toFixed(2)} />
            <g transform={`rotate(${(-2 + 5 * wave(view.t, 2_400)).toFixed(1)})`}>
              <ellipse cx="0" cy="0.4" rx="2.5" ry="3.3" transform="rotate(18)" fill={`url(#${id}seed)`} />
              <ellipse cx="-0.8" cy="-0.6" rx=".7" ry="1.2" transform="rotate(18)" fill="#ffffff" opacity=".6" />
              <path d="M0.9 -2.8 C 1.4 -4.4, 2.8 -4.6, 3.4 -3.9" fill="none" stroke={g.leafHi} strokeWidth=".55" strokeLinecap="round" />
            </g>
          </g>
        )}

        {/* the vine: shadow, stem, a lit edge; then its leaves; then the growing tip */}
        <g opacity={st === 3 ? 0.95 : 1}>
          <path d={stem.outline} fill="#000" opacity=".35" transform="translate(0.5 0.9)" />
          <path d={stem.outline} fill={g.stem} stroke={g.stemDark} strokeWidth=".55" strokeLinejoin="round" />
          <path d={stem.spine} fill="none" stroke={g.leafHi} strokeOpacity=".45" strokeWidth=".55" strokeLinecap="round" transform="translate(-0.3 -0.45)" />
          {leaves.map((l) => (
            <g key={l.key} transform={`translate(${l.x.toFixed(2)} ${l.y.toFixed(2)}) rotate(${l.rot.toFixed(1)}) scale(${l.s.toFixed(3)})`}>
              {root ? (
                <>
                  <path d="M0 0 Q 3 -1.3 5.6 -0.4 Q 7.6 0.4 9.2 -1.4" fill="none" stroke={g.leaf} strokeWidth="1" strokeLinecap="round" />
                  <path d="M3.4 -0.8 Q 4.4 -2.6 5.8 -3.2" fill="none" stroke={g.leafHi} strokeWidth=".6" strokeLinecap="round" opacity=".8" />
                </>
              ) : (
                <>
                  <path d={LEAF_D} fill={`url(#${id}leaf)`} stroke={g.stemDark} strokeWidth=".35" />
                  <path d="M0.4 0 Q 5 -0.4 9.2 0" fill="none" stroke={g.stemDark} strokeWidth=".45" opacity=".6" />
                </>
              )}
            </g>
          ))}
          <g transform={`translate(${pts[0]!.x.toFixed(2)} ${pts[0]!.y.toFixed(2)}) rotate(${ANGLE[view.d]})`}>
            {root ? (
              <path d="M-1.2 -2 C 1.6 -2.2, 4 -0.8, 5.2 0 C 4 0.8, 1.6 2.2, -1.2 2 Z" fill={g.leafHi} stroke={g.stemDark} strokeWidth=".4" />
            ) : (
              <>
                <path d="M0.5 -0.8 C 1.2 -3.6, 3.6 -4.6, 4.6 -3.4 C 5.3 -2.5, 4.4 -1.6, 3.7 -2.3" fill="none" stroke={g.stem} strokeWidth=".6" strokeLinecap="round" />
                <path d="M-1.4 -2 C 1.6 -2.6, 4 -1.2, 4.6 0 C 4 1.2, 1.6 2.6, -1.4 2 Z" fill={g.leafHi} stroke={g.stemDark} strokeWidth=".4" />
                <path d="M-0.6 1.9 C 1.8 1.4, 3.6 0.6, 4.6 0" fill="none" stroke={g.leaf} strokeWidth=".9" />
                <ellipse cx="1" cy="-0.9" rx="1.5" ry=".55" fill="#ffffff" opacity=".45" />
              </>
            )}
          </g>
        </g>
      </g>

      {/* the arrows under the bed */}
      {PAD.map((b, i) => (
        <BoardButton
          key={b.d}
          x={b.x}
          y={b.y}
          w={PAD_W}
          h={PAD_H}
          label={say(lang, ARROW_WORDS[b.d][0], ARROW_WORDS[b.d][1])}
          accent={accent}
          down={view.k === b.d}
          dim={st === 3}
          onPress={input ? () => input.press?.(i) : undefined}
        >
          <path d={ARROW[b.d]} transform="scale(1.6)" fill={accent} />
        </BoardButton>
      ))}
    </BoardFrame>
  );
}

/** The bed the vine grows in, by era: garden soil, a lit tray, scorched earth, sand, loam. In bed units. */
function Bed({ era, id }: { era: Era; id: string }) {
  const g = GROUND[era];
  const W = COLS * C;
  const H = ROWS * C;
  return (
    <g>
      <rect x="0" y="0" width={W} height={H} rx="2" fill={`url(#${id}soil)`} />
      {era === 'stars' &&
        Array.from({ length: ROWS - 1 }, (_, r) => (
          <path key={r} d={`M0 ${(r + 1) * C} Q ${W / 4} ${(r + 1) * C - 0.8} ${W / 2} ${(r + 1) * C} T ${W} ${(r + 1) * C}`} fill="none" stroke="#000" strokeOpacity=".35" strokeWidth=".8" />
        ))}
      {era === 'neon' && (
        <g stroke={g.line} strokeOpacity=".13" strokeWidth=".3">
          {Array.from({ length: COLS - 1 }, (_, c) => (
            <line key={`c${c}`} x1={(c + 1) * C} y1="0" x2={(c + 1) * C} y2={H} />
          ))}
          {Array.from({ length: ROWS - 1 }, (_, r) => (
            <line key={`r${r}`} x1="0" y1={(r + 1) * C} x2={W} y2={(r + 1) * C} />
          ))}
        </g>
      )}
      {era === 'debris' && (
        <g fill="none" stroke={g.line} strokeOpacity=".16" strokeWidth=".45" strokeLinejoin="round">
          <path d="M8 0 L12 9 L9 16 L15 25" />
          <path d="M62 70 L58 60 L64 52 L60 44" />
          <path d="M100 22 L91 26 L86 34 L78 35" />
        </g>
      )}
      {era === 'dust' &&
        [8, 22, 36, 50, 64].map((y) => (
          <path key={y} d={`M0 ${y} Q 12 ${y - 2.4} 25 ${y} T 50 ${y} T 75 ${y} T 100 ${y}`} fill="none" stroke={g.line} strokeOpacity=".12" strokeWidth=".6" />
        ))}
      {era === 'canopy' &&
        SPECKS.slice(0, 7).map((s, i) => (
          <g key={i} fill={g.leaf} opacity=".22">
            <circle cx={s.x * W} cy={s.y * H} r="1.6" />
            <circle cx={s.x * W + 1.6} cy={s.y * H + 0.6} r="1.2" />
            <circle cx={s.x * W - 1.2} cy={s.y * H + 0.9} r="1" />
          </g>
        ))}
      {SPECKS.map((s, i) => (
        <circle key={i} cx={s.x * W} cy={s.y * H} r={s.r * 0.5} fill={g.fleck} opacity=".8" />
      ))}
      {/* a faint dot in every cell, so the turns can be judged */}
      <g fill={g.line} opacity=".16">
        {Array.from({ length: COLS * ROWS }, (_, i) => (
          <circle key={i} cx={(i % COLS) * C + C / 2} cy={Math.floor(i / COLS) * C + C / 2} r=".38" />
        ))}
      </g>
      <rect x="0" y="0" width={W} height={H} rx="2" fill="none" stroke="#000" strokeOpacity=".5" strokeWidth="1.2" />
    </g>
  );
}

// The bed stays the same from frame to frame: drawn once, not sixty times a second.
const BedArt = memo(Bed);

const KEYS: Record<string, Dir> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

function Board(props: RoundProps) {
  const { level, lang } = props;
  const fb = useRef<Feedback | null>(null);
  const [game] = useState(() => vineRound(level, seeded(Date.now() + level * 7919), feedbackTo(fb)));
  const round = useRound(props, (_dt, now) => game.step(now));
  fb.current = round;

  // Arrow keys too, for the practice page on a laptop.
  const latest = useRef(round);
  latest.current = round;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const d = KEYS[e.key];
      if (!d) return;
      e.preventDefault();
      if (latest.current.live) game.press(d, latest.current.time());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [game]);

  return (
    <View
      view={round.show(game.view(round.now, round.fx))}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Swipe to steer the vine', 'Sveip for å styre ranken'),
        down: (p) => round.live && game.input.down(p),
        move: (p) => round.live && game.input.move(p, round.time()),
        up: () => game.input.up(),
        press: (i) => {
          const d = PAD[i]?.d;
          if (d && round.live) game.press(d, round.time());
        },
      }}
    />
  );
}

/**
 * The how-to, grown by the rules themselves: round 1's pace, the seeds laid by hand. The finger
 * swipes up and then right to steer the vine onto the first seed, then taps the down and right
 * arrows to bring it onto the second.
 */
function demoGrowth(): DemoScript<VineView> {
  const ms = 6_400;
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 300, hide: true },
    { at: 600, x: 262, y: 300, hide: false },
    { at: 1_250, x: 205, y: 165 },
    { at: 1_450, x: 205, y: 165, down: true },
    { at: 1_700, x: 205, y: 112, ease: 'linear' },
    { at: 1_780, x: 205, y: 112, down: false },
    { at: 2_150, x: 140, y: 110 },
    { at: 2_300, x: 140, y: 110, down: true },
    { at: 2_550, x: 205, y: 110, ease: 'linear' },
    { at: 2_630, x: 205, y: 110, down: false },
    { at: 3_050, x: 160, y: 288 },
    { at: 3_250, x: 160, y: 288, down: true },
    { at: 3_400, x: 160, y: 288, down: false },
    { at: 4_150, x: 250, y: 288 },
    { at: 4_450, x: 250, y: 288, down: true },
    { at: 4_600, x: 250, y: 288, down: false },
    { at: 5_500, x: 280, y: 312, hide: true },
  ];
  const round = demoRound();
  const game = vineRound(0, seeded(1), round, { seeds: [{ x: 5, y: 1 }, { x: 7, y: 4 }, { x: 3, y: 5 }], startMs: 600 });
  const keys = playDemo(
    ms,
    finger,
    round,
    {
      input: {
        down: (p, now) => {
          const d = padAt(p);
          if (d) game.press(d, now);
          else game.input.down(p);
        },
        move: (p, now) => game.input.move(p, now),
        up: () => game.input.up(),
      },
      step: (_dt, now) => game.step(now),
      view: (now) => game.view(now, round.fx()),
    },
    ({ t: _t, p: _p, fx: _fx, ...parts }) => parts,
  );
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'SWIPE TO TURN THE VINE', no: 'SVEIP FOR Å SNU RANKEN' },
      { at: 2_950, en: 'OR TAP THE ARROWS', no: 'ELLER TRYKK PÅ PILENE' },
      { at: 4_650, en: 'GROW IT ONTO EACH SEED', no: 'LA DEN GRO INN PÅ HVERT FRØ' },
    ],
  };
}

export const game = thirtyGame<VineView>({
  Board,
  View,
  demo: demoGrowth(),
  // Nothing to get wrong since no round lays rocks: no mistake count under the board.
  mistakes: false,
  words: {
    en: {
      instruction: 'Swipe or tap the arrows to grow the vine onto 3 seeds. Each round a little quicker.',
      howMove: 'Swipe on the board, or tap the arrows, to turn the vine. Grow it onto each seed.',
    },
    no: {
      instruction: 'Sveip eller trykk på pilene for å gro ranken til 3 frø. Litt raskere hver runde.',
      howMove: 'Sveip på brettet, eller trykk på pilene, for å snu ranken. La den gro inn på hvert frø.',
    },
  },
});
